import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../supabase/client';
import './SavedReports.css';

interface Report {
  id: string;
  created_at: string;
  title: string;
  content: string;
  address: string;
  zone_code: string;
  report_id: string;
  user_id: string;
  pdf_url?: string;
  pdf_path?: string;
}

const SavedReports: React.FC = () => {
  const { user } = useAuth();
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set());

  const fetchReports = async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const { data, error } = await supabase
        .from('reports')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      
      // Deduplicate reports by report_id (same report generated multiple times)
      const uniqueReports = (data || []).filter((report, index, self) => 
        index === self.findIndex(r => r.report_id === report.report_id)
      );
      setReports(uniqueReports);
    } catch (error: any) {
      console.error('Error fetching reports:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();

    if (!user) return;
    const channel = supabase.channel(`user-${user?.id || 'reports'}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'reports', filter: `user_id=eq.${user.id}` }, (payload) => {
        if (payload.eventType === 'INSERT') {
          setReports(prev => {
            const newReport = payload.new as Report;
            // Check if report with same report_id already exists to prevent duplicates
            const existingReport = prev.find(r => r.report_id === newReport.report_id);
            if (existingReport) {
              return prev; // Report already exists, don't add duplicate
            }
            return [newReport, ...prev];
          });
        } else if (payload.eventType === 'DELETE') {
          setReports(prev => prev.filter(r => r.id !== (payload.old as any).id));
        } else if (payload.eventType === 'UPDATE') {
          setReports(prev => prev.map(r => r.id === (payload.new as any).id ? (payload.new as Report) : r));
        }
      })
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  useEffect(() => {
    document.body.classList.add('reports-open');
    return () => {
      document.body.classList.remove('reports-open');
    };
  }, []);

  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === 'reports_refresh') {
        fetchReports();
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const deleteReport = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this report?')) {
      return;
    }

    try {
      setDeletingIds(prev => new Set(prev).add(id));
      
      const { error } = await supabase
        .from('reports')
        .delete()
        .eq('id', id);
        
      if (error) throw error;
      
      setReports(reports.filter(report => report.id !== id));
    } catch (error: any) {
      console.error('Error deleting report:', error);
      setError(`Failed to delete report: ${error.message}`);
    } finally {
      setDeletingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  const downloadPDF = async (report: Report) => {
    // Skip stored pdf_url for now since they tend to expire
    // Always generate fresh signed URLs for reliable downloads

    if (!report.pdf_path) {
      setError('No PDF available for this report - cloud storage required');
      return;
    }

    try {
      setDownloadingIds(prev => new Set(prev).add(report.id));

      // Generate fresh signed URL from cloud storage
      // Use individual user bucket (user-{userId}) to match server-side storage
      const userBucket = `user-${user?.id}`;
      const { data, error } = await supabase
        .storage
        .from(userBucket)
        .createSignedUrl(report.pdf_path, 3600); // 1 hour expiry
      
      if (error) throw error;

      await triggerBrowserDownload(data.signedUrl, report);
    } catch (error: any) {
      console.error('Error downloading PDF:', error);
      setError(`Failed to download PDF: ${error.message}`);
    } finally {
      setDownloadingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(report.id);
        return newSet;
      });
    }
  };

  const triggerBrowserDownload = async (url: string, report: Report) => {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Failed to download PDF: ${response.status} ${response.statusText}`);
    const blob = await response.blob();
    const objUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objUrl;
    link.download = `ZoningPal-Report-${report.report_id}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objUrl);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (!user) {
    return (
      <div className="saved-reports-empty">
        <div className="empty-state">
          <h3>Sign In Required</h3>
          <p>Please sign in to view your saved reports</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="saved-reports-loading">
        <div className="loading-spinner"></div>
        <div className="loading-message">Loading saved reports...</div>
      </div>
    );
  }

  if (reports.length === 0) {
    return (
      <div className="saved-reports-empty">
        <div className="empty-state">
          <h3>No Saved Reports</h3>
          <p>Generate and save a zoning analysis to see it here</p>
        </div>
      </div>
    );
  }

  return (
    <div className="saved-reports-list">
      {reports.map(report => (
        <div key={report.id} className="saved-report-item">
          <div className="saved-report-info">
            <h4 className="saved-report-title">{report.address}</h4>
            <div className="saved-report-meta">
              <span className="saved-report-badge">{report.zone_code}</span>
              <span className="saved-report-id">{report.report_id || `ZP-${report.id.slice(0, 8)}`}</span>
            </div>
            <div className="saved-report-date">{formatDate(report.created_at)}</div>
          </div>
          
          <div className="saved-report-actions">
            <button 
              className="saved-report-btn download" 
              onClick={() => downloadPDF(report)}
              disabled={downloadingIds.has(report.id)}
            >
              {downloadingIds.has(report.id) ? 'Downloading...' : 'Download'}
            </button>
            <button 
              className="saved-report-btn delete" 
              onClick={() => deleteReport(report.id)}
              disabled={deletingIds.has(report.id)}
            >
              {deletingIds.has(report.id) ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      ))}
      
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
    </div>
  );
};

export default SavedReports;
